import { useReducer, useState } from 'react';

import { SpecializedAgent } from '@frontend/types';

// Initial state for creating a new agent
const initialAgentState: SpecializedAgent = {
  id: '',
  name: '',
  agent_id: '',
  description: '',
  tag: 'Other', // TODO: add tag
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  is_active: true,
  inference_type: 'simple',
  creativity: 0.5,
  // @ts-expect-error: FIXME: OPAL-2388
  prompt_template: undefined,
  version: '',
  parameters: [],
  output: {
    type: 'string',
    description: '',
    schema: {},
  },
  baseline_evaluation_score: 80,
  is_enabled_in_chat: true,
  links: {
    self: '',
    collection: '',
    executions: '',
    execute: '',
    delete: '',
  },
};

export type SpecializedAgentAction =
  | { type: 'SET_ALL'; payload: SpecializedAgent }
  | { type: 'RESET' }
  | {
      type: 'UPDATE_FIELD';
      field: keyof SpecializedAgent;
      value: SpecializedAgent[keyof SpecializedAgent];
    }
  | {
      type: 'UPDATE_NESTED';
      field: keyof SpecializedAgent;
      value: SpecializedAgent[keyof SpecializedAgent];
    }
  | { type: 'UPDATE_OUTPUT'; value: Partial<SpecializedAgent['output']> };

function agentReducer(state: SpecializedAgent, action: SpecializedAgentAction): SpecializedAgent {
  switch (action.type) {
    case 'SET_ALL':
      return { ...action.payload };
    case 'RESET':
      return { ...initialAgentState };
    case 'UPDATE_FIELD':
      return { ...state, [action.field]: action.value };
    case 'UPDATE_NESTED':
      // For arrays or objects like parameters, file_urls, enabled_tools
      return { ...state, [action.field]: action.value };
    case 'UPDATE_OUTPUT': {
      const prevOutput = state.output ?? { type: 'string', description: '', schema: {} };
      const value = action.value ?? {};
      return {
        ...state,
        output: {
          ...prevOutput,
          ...value,
          type: value.type ?? prevOutput.type,
          description: value.description ?? prevOutput.description,
          schema: value.schema ?? prevOutput.schema,
        },
      };
    }
    default:
      return state;
  }
}

export function useSpecializedAgentReducer(initial?: SpecializedAgent) {
  const [formData, dispatch] = useReducer(agentReducer, initial ?? initialAgentState);
  const [hasError, setHasError] = useState(false);
  return [formData, dispatch, hasError, setHasError] as const;
}
